import { v4 as uuid } from 'uuid';
import {
  memberWebAppOnBeforeAction,
  memberWebAppScreenOpen as memberWebAppScreenOpenEvent,
  memberWebOnAfterAction,
  memberWebAppButtonClick,
} from '@wix/bi-logger-challenges-member-web/v2';
import type { memberWebAppScreenOpenParams } from '@wix/bi-logger-challenges-member-web/v2/types';
import { ActionTypes, ScreenNames } from '../contexts/main/biInterfaces';
import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import type { ParticipantStep } from '../types/v3Types';
import { getStepType } from '../selectors/step';

export interface ScreenOpenBiParams {
  step?: string;
  additionalDetails?: string;
}

// The 600 schema has `additionalDetails`, but this logger's type predates it.
// The logger sends params as given, to the same endpoint as every other 600
export const memberWebAppScreenOpen = (
  params: memberWebAppScreenOpenParams & ScreenOpenBiParams,
) => memberWebAppScreenOpenEvent(params as memberWebAppScreenOpenParams);

// Step view context for 600. `isAiTutorAvailable` is left out while it is
// unknown (unknown is not false), and a key without a value is never sent
export const getScreenOpenBiParams = ({
  step,
  isAiTutorAvailable,
}: {
  step?: ParticipantStep | null;
  isAiTutorAvailable?: boolean;
}): ScreenOpenBiParams => {
  const stepType = step ? getStepType(step.source) : undefined;
  const details = {
    ...(isAiTutorAvailable !== undefined && { isAiTutorAvailable }),
    ...(stepType && { stepType }),
  };

  return {
    ...(step?.id && { step: step.id }),
    ...(Object.keys(details).length && {
      additionalDetails: JSON.stringify(details),
    }),
  };
};

export const biBeforeAndAfter = async <T extends (...args: any) => any>(
  bi: any,
  actionTypeMaybeArray: ActionTypes | ActionTypes[],
  fn: T,
): Promise<ReturnType<T>> => {
  const actionId = uuid();
  const actionTypesList = Array.isArray(actionTypeMaybeArray)
    ? actionTypeMaybeArray
    : [actionTypeMaybeArray];

  for (const actionType of actionTypesList) {
    bi.report(
      memberWebAppOnBeforeAction({
        actionId,
        actionType,
        // screenName,
      }),
    );
  }

  const response = await fn(actionId);

  for (const actionType of actionTypesList.slice().reverse()) {
    bi.report(
      memberWebOnAfterAction({
        actionId,
        actionType,
        // screenName,
      }),
    );
  }

  return response;
};

// For flows that report failure by resolving instead of throwing: reports the
// before-action now and returns a callback that reports the after-action on
// success only
export const biBeforeAction = (bi: any, actionType: ActionTypes) => {
  const actionId = uuid();
  bi.report(memberWebAppOnBeforeAction({ actionId, actionType }));

  return () => {
    bi.report(memberWebOnAfterAction({ actionId, actionType }));
  };
};

export const biButtonClick = async (
  flowApi: Pick<ControllerFlowAPI, 'bi'>,
  buttonName: string,
) => {
  return flowApi.bi.report(
    memberWebAppButtonClick({
      buttonName,
    }),
  );
};

export const biChangePage = async (
  bi: any,
  screenName: ScreenNames,
  screenOpenBiParams: ScreenOpenBiParams = {},
) => {
  await bi.report(
    memberWebAppScreenOpen({
      screenName,
      ...screenOpenBiParams,
    }),
  );

  // Only the screen name is a default: step context belongs to this event
  await bi.updateDefaults({
    screenName,
  });
};
